import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { UserService } from '../../services/user.service';
import { TokenStorageService } from '../../services/storage/token-storage.service';
import { environment } from '../../../environments/environment';
import { AppConfigService } from '../../services/config/app-config.service';

@Component({
    selector: 'app-upload-zip',
    templateUrl: './upload-zip.component.html',
    styleUrl: './upload-zip.component.css',
    standalone: false
})
export class UploadZipComponent implements OnInit {
  form: FormGroup = new FormGroup({});
  submitted: boolean = false;
  loading: boolean = false;
  uploading: boolean = false;
  uploadProgress: number = 0;
  selectedFile: File | null = null;
  fileName: string = '';
  user: any;
  pipelineStatus: string = '';
  pipelineId: number | null = null;
  pipelineUrl: string = '';
  pipelineTriggerUrl: string = 'https://gitlab.com/gtn-tech/fintech-api/portal-and-docs-deployment-core/-/pipelines';
  uploadSuccess: boolean = false;
  fileKey: string = '';
  bucketPath: string = '';
  lastBucketPathUsed: string = '';
  
  // Project and Version dropdowns
  projects: string[] = [];
  selectedProject: string = '';
  availableVersions: string[] = [];
  selectedVersion: string = '';
  private readonly renamedUploadFileName = 'space-export.zip';

  // Version mapping for each project
  projectVersions: { [key: string]: string[] } = {};
  private readonly defaultProjectVersions: { [key: string]: string[] } = {
    'tradeapi': ['1.2.1', '1.3.0'],
    'microinvestapi': ['1.2.0', '1.1.0'],
    'investapi': ['1.0.0']
  };

  constructor(
    private formBuilder: FormBuilder,
    private toastr: ToastrService,
    private userService: UserService,
    private tokenStorage: TokenStorageService,
    private appConfigService: AppConfigService
  ) { }

  ngOnInit(): void {
    this.initializeForm();
    this.user = this.tokenStorage.getUser();
    const config = this.appConfigService.getCommonConfig;
    this.projectVersions = config?.projectVersions || this.defaultProjectVersions;
    this.projects = Object.keys(this.projectVersions);
    
    // Watch for project changes
    this.form.get('project')?.valueChanges.subscribe(project => {
      if (project) {
        this.onProjectChange(project);
      } else {
        this.availableVersions = [];
        this.form.patchValue({ version: '' });
      }
    });
  }

  initializeForm(): void {
    // Hardcoded bucket path
    this.bucketPath = environment.defaultS3BucketPath || 'confluence-to-markdown/';
    this.form = this.formBuilder.group({
      project: ['', [Validators.required]],
      version: ['', [Validators.required]],
      file: [null, [Validators.required]]
    });
  }

  get formControl(): { [key: string]: AbstractControl } {
    return this.form.controls;
  }

  onFileSelected(event: any): void {
    const file: File = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.name.toLowerCase().endsWith('.zip')) {
        this.toastr.error('Please select a ZIP file only', 'Invalid File Type');
        event.target.value = '';
        return;
      }
      // Rename uploaded file to the expected filename before upload
      this.selectedFile = this.renameFile(file);
      this.fileName = this.renamedUploadFileName;
      this.form.patchValue({ file: this.selectedFile });
      this.uploadSuccess = false;
      this.pipelineStatus = '';
      this.pipelineId = null;
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (!file.name.toLowerCase().endsWith('.zip')) {
        this.toastr.error('Please drop a ZIP file only', 'Invalid File Type');
        return;
      }
      // Rename uploaded file to the expected filename before upload
      this.selectedFile = this.renameFile(file);
      this.fileName = this.renamedUploadFileName;
      this.form.patchValue({ file: this.selectedFile });
      this.uploadSuccess = false;
      this.pipelineStatus = '';
      this.pipelineId = null;
    }
  }

  onClickUpload(): void {
    this.submitted = true;
    this.uploadSuccess = false;
    this.pipelineStatus = '';
    this.pipelineId = null;
    this.pipelineUrl = '';

    if (this.form.invalid || !this.selectedFile) {
      const projectValue = this.form.get('project')?.value;
      const versionValue = this.form.get('version')?.value;
      
      if (!projectValue) {
        this.toastr.error('Please select a project', 'Upload Failed');
      } else if (!versionValue) {
        this.toastr.error('Please select a version', 'Upload Failed');
      } else if (!this.selectedFile) {
        this.toastr.error('Please select a ZIP file', 'Upload Failed');
      }
      return;
    }

    this.uploadFile();
  }

  private uploadFile(): void {
    if (!this.selectedFile) {
      return;
    }

    this.loading = true;
    this.uploading = true;
    this.uploadProgress = 0;
    const selectedProject = this.form.get('project')?.value;
    const selectedVersion = this.form.get('version')?.value;
    const bucketPathWithVersion = this.getBucketPathWithVersion(selectedProject, selectedVersion);
    this.lastBucketPathUsed = bucketPathWithVersion;

    // Generate presigned URL
    this.userService.generateS3UploadUrl(
      bucketPathWithVersion,
      this.selectedFile.name,
      this.form.get('project')?.value,
      this.form.get('version')?.value
    ).subscribe({
      next: (response) => {
        if (response.presignedUrl && response.fileKey) {
          this.fileKey = response.fileKey;
          // Upload file to S3
          this.userService.uploadToS3(response.presignedUrl, this.selectedFile!).subscribe({
            next: (event: any) => {
              if (event.type === 'progress') {
                this.uploadProgress = event.percentComplete;
              } else if (event.type === 'complete') {
                this.uploadProgress = 100;
                this.uploadSuccess = true;
                this.uploading = false;
                this.toastr.success('File uploaded successfully to S3', 'Upload Complete');
                this.pipelineUrl = this.pipelineTriggerUrl;
              }
            },
            error: (err) => {
              this.loading = false;
              this.uploading = false;
              this.uploadProgress = 0;
              this.toastr.error(err?.statusText || err?.message || 'Failed to upload file to S3', 'Upload Failed');
            }
          });
        } else {
          this.loading = false;
          this.uploading = false;
          this.toastr.error('Invalid response from server', 'Error');
        }
      },
      error: (err) => {
        this.loading = false;
        this.uploading = false;
        this.uploadProgress = 0;
        this.toastr.error(err?.error?.message || 'Failed to generate presigned URL', 'Error');
      }
    });
  }

  private renameFile(file: File): File {
    // Force the upload filename to the expected value regardless of the original name
    return new File([file], this.renamedUploadFileName, { type: file.type });
  }

  onProjectChange(project: string): void {
    this.selectedProject = project;
    this.availableVersions = this.projectVersions[project] || [];
    this.selectedVersion = '';
    this.form.patchValue({ version: '' });
  }

  resetForm(): void {
    this.form.reset({
      project: '',
      version: '',
      file: null
    });
    this.selectedFile = null;
    this.fileName = '';
    this.selectedProject = '';
    this.selectedVersion = '';
    this.availableVersions = [];
    this.uploadSuccess = false;
    this.pipelineStatus = '';
    this.pipelineId = null;
    this.pipelineUrl = '';
    this.uploadProgress = 0;
    this.submitted = false;
    this.lastBucketPathUsed = '';
  }

  private getBucketPathWithVersion(project: string, version: string): string {
    const basePath = this.bucketPath.endsWith('/') ? this.bucketPath : `${this.bucketPath}/`;
    // Both project and version are required, so always construct path with both
    return `${basePath}${project}/${version}/`;
  }
}

